import update from 'immutability-helper';
import moment from 'moment';
import { ActionType, defaultAction } from 'src/redux/types';
import { DEFAULT_VALUE, MAX_STEPS } from '../constants';
import {
  FormActivationData,
  FormActivationRequest,
  PaymentMethods,
} from '../types';
import { ACTION_TYPES } from './types';

export type FormActivationState = {
  isLoading: boolean;
  error?: string;
  data: FormActivationData;
};

export type ActiveEWallet = {
  isLoading: boolean;
  error?: string;
  data?: PaymentMethods;
};

export type FormPaymentActivationState = {
  currentPaymentActivationStep: number;
  formActivation: FormActivationState;
  activeEWallet: ActiveEWallet;
  isSubmitting: boolean;
};

const initialState: FormPaymentActivationState = {
  currentPaymentActivationStep: 1,
  formActivation: {
    data: DEFAULT_VALUE,
    error: undefined,
    isLoading: false,
  },
  activeEWallet: {
    isLoading: false,
    error: undefined,
    data: undefined,
  },
  isSubmitting: false,
};

export default (
  state = initialState,
  { payload, type }: ActionType = defaultAction,
) => {
  switch (type) {
    case ACTION_TYPES.FORM_PAYMENT_ACTIVATION_RESET_STATE:
      return initialState;
    case ACTION_TYPES.FORM_PAYMENT_ACTIVATION_SET_CURRENT_STEP: {
      let { currentPaymentActivationStep } = state;
      if (payload >= 0 && payload <= MAX_STEPS - 1) {
        currentPaymentActivationStep = payload;
      }
      return update(state, {
        currentPaymentActivationStep: {
          $set: currentPaymentActivationStep,
        },
      });
    }
    case ACTION_TYPES.FORM_PAYMENT_ACTIVATION_SET_CURRENT_STEP_NEXT: {
      const { currentPaymentActivationStep } = state;
      return update(state, {
        currentPaymentActivationStep: {
          $set: currentPaymentActivationStep + 1,
        },
      });
    }
    case ACTION_TYPES.FORM_PAYMENT_ACTIVATION_SET_CURRENT_STEP_BACK: {
      const { currentPaymentActivationStep } = state;
      return update(state, {
        currentPaymentActivationStep: {
          $set: currentPaymentActivationStep - 1,
        },
      });
    }

    case ACTION_TYPES.FORM_PAYMENT_ACTIVATION_UPDATE: {
      return update(state, { formActivation: { data: { $merge: payload } } });
    }

    case ACTION_TYPES.FORM_PAYMENT_ACTIVATION_LOAD_DATA: {
      return update(state, {
        formActivation: {
          isLoading: {
            $set: true,
          },
        },
      });
    }
    case ACTION_TYPES.FORM_PAYMENT_ACTIVATION_SAVE_DATA:
    case ACTION_TYPES.FORM_PAYMENT_ACTIVATION_SUBMIT_DATA: {
      return update(state, {
        isSubmitting: {
          $set: true,
        },
      });
    }
    case ACTION_TYPES.FORM_PAYMENT_ACTIVATION_SAVE_DATA_FAILED:
    case ACTION_TYPES.FORM_PAYMENT_ACTIVATION_SUBMIT_DATA_FAILED:
    case ACTION_TYPES.FORM_PAYMENT_ACTIVATION_SAVE_DATA_SUCCESS:
    case ACTION_TYPES.FORM_PAYMENT_ACTIVATION_SUBMIT_DATA_SUCCESS: {
      return update(state, {
        isSubmitting: {
          $set: false,
        },
      });
    }

    case ACTION_TYPES.FORM_PAYMENT_ACTIVATION_LOAD_DATA_SUCCESS: {
      const { data }: { data: FormActivationRequest } = payload;
      return update(state, {
        formActivation: {
          data: {
            $set: {
              ewalletApplied: data.ewallet_applied || [],
              ewalletSubmitted: [],
              ewalletAlreadyApplied: [],
              ownerName: data.owner_name || '',
              ownerKTP: data.owner_ktp || '',
              ownerBirthPlace: data.owner_birth_place,
              ownerBirthDate: data.owner_birth_date
                ? moment(data.owner_birth_date, 'DD/MM/YYYY').toDate()
                : undefined,
              ownerNpwp: data.owner_npwp || '',
              ownerNpwpType: data.owner_npwp_type || '',
              ownerOccupation: data.owner_occupation || '',
              ownerGender: data.owner_gender || '',
              ownerAddressProvince: data.owner_address_province || '',
              ownerAddressCity: data.owner_address_city || '',
              ownerAddressDistrict: data.owner_address_district || '',
              ownerAddressSubDistrict: data.owner_address_sub_district || '',
              ownerAddressPostalCode: data.owner_address_postal_code || '',
              ownerAddress: data.owner_address || '',
              ownerEmail: data.owner_email || '',
              ownerPhoneNumber: data.owner_phone_number || '',
              npwpAddress: data.npwp_address || '',
              npwpAddressProvince: data.npwp_address_province || '',
              npwpAddressCity: data.npwp_address_city || '',
              npwpAddressDistrict: data.npwp_address_district || '',
              npwpAddressSubDistrict: data.npwp_address_sub_district || '',
              npwpAddressPostalCode: data.npwp_address_postal_code || '',
              picEmail: data.pic_email || '',
              picPosition: data.pic_position || '',
              picName: data.pic_name || '',
              picPhoneNumber: data.pic_phone_number || '',
              isTaxable: data.is_taxable?.toString(),
              businessLink: data.business_link || '',
              flowPayment: data.payment_flow || '',
              businessLogo: data.business_logo || '',
              businessPermitNumber: data.business_permit_number || '',
              hasValidityPeriod: data.has_validity_period || '',
              businessPermitValidityPeriod: data.business_permit_validity_period
                ? moment(
                    data.business_permit_validity_period,
                    'DD/MM/YYYY',
                  ).toDate()
                : undefined,
              businessPermitIssuedDate: data.business_permit_issued_date
                ? moment(
                    data.business_permit_issued_date,
                    'DD/MM/YYYY',
                  ).toDate()
                : undefined,
              businessEstablishedDate: data.business_established_date
                ? moment(data.business_established_date, 'DD/MM/YYYY').toDate()
                : undefined,
              businessPlace: data.business_permit_established_place || '',
              businessAddress: data.business_address || '',
              businessAddressProvince: data.business_address_province || '',
              businessAddressCity: data.business_address_city || '',
              businessAddressDistrict: data.business_address_district || '',
              businessAddressSubDistrict:
                data.business_address_sub_district || '',
              businessAddressPostalCode:
                data.business_address_postal_code || '',
              longitude: data.longitude || '',
              latitude: data.latitude || '',
              omsetTransactionExpectation: data.expected_total_sales || '',
              numberTransactionExpectation: data.expected_transactions || '',
            },
          },
          isLoading: {
            $set: false,
          },
        },
      });
    }

    case ACTION_TYPES.FORM_PAYMENT_ACTIVATION_LOAD_DATA_FAILED: {
      return update(state, {
        formActivation: {
          error: {
            $set: payload.message,
          },
          isLoading: {
            $set: false,
          },
        },
      });
    }

    case ACTION_TYPES.FORM_PAYMENT_ACTIVATION_SET_DATA: {
      const data: FormActivationRequest = payload;
      return update(state, {
        formActivation: {
          data: {
            $merge: {
              ewalletApplied: data.ewallet_applied || [],
              ewalletSubmitted: [],
              ownerName: data.owner_name || '',
              ownerKTP: data.owner_ktp || '',
              ownerBirthPlace: data.owner_birth_place,
              ownerBirthDate: data.owner_birth_date
                ? moment(data.owner_birth_date, 'DD/MM/YYYY').toDate()
                : undefined,
              ownerNpwp: data.owner_npwp || '',
              ownerNpwpType: data.owner_npwp_type || '',
              ownerOccupation: data.owner_occupation || '',
              ownerGender: data.owner_gender || '',
              ownerAddressProvince: data.owner_address_province || '',
              ownerAddressCity: data.owner_address_city || '',
              ownerAddressDistrict: data.owner_address_district || '',
              ownerAddressSubDistrict: data.owner_address_sub_district || '',
              ownerAddressPostalCode: data.owner_address_postal_code || '',
              ownerAddress: data.owner_address || '',
              ownerEmail: data.owner_email || '',
              ownerPhoneNumber: data.owner_phone_number || '',
              npwpAddress: data.npwp_address || '',
              npwpAddressProvince: data.npwp_address_province || '',
              npwpAddressCity: data.npwp_address_city || '',
              npwpAddressDistrict: data.npwp_address_district || '',
              npwpAddressSubDistrict: data.npwp_address_sub_district || '',
              npwpAddressPostalCode: data.npwp_address_postal_code || '',
              picEmail: data.pic_email || '',
              picPosition: data.pic_position || '',
              picName: data.pic_name || '',
              picPhoneNumber: data.pic_phone_number || '',
              isTaxable: data.is_taxable?.toString(),
              businessLink: data.business_link || '',
              flowPayment: data.payment_flow || '',
              businessLogo: data.business_logo || '',
              businessPermitNumber: data.business_permit_number || '',
              hasValidityPeriod: data.has_validity_period || '',
              businessPermitValidityPeriod: data.business_permit_validity_period
                ? moment(
                    data.business_permit_validity_period,
                    'DD/MM/YYYY',
                  ).toDate()
                : undefined,
              businessPermitIssuedDate: data.business_permit_issued_date
                ? moment(
                    data.business_permit_issued_date,
                    'DD/MM/YYYY',
                  ).toDate()
                : undefined,
              businessEstablishedDate: data.business_established_date
                ? moment(data.business_established_date, 'DD/MM/YYYY').toDate()
                : undefined,
              businessPlace: data.business_permit_established_place || '',
              businessAddress: data.business_address || '',
              businessAddressProvince: data.business_address_province || '',
              businessAddressCity: data.business_address_city || '',
              businessAddressDistrict: data.business_address_district || '',
              businessAddressSubDistrict:
                data.business_address_sub_district || '',
              businessAddressPostalCode:
                data.business_address_postal_code || '',
              longitude: data.longitude || '',
              latitude: data.latitude || '',
              omsetTransactionExpectation: data.expected_total_sales || '',
              numberTransactionExpectation: data.expected_transactions || '',
            },
          },
        },
      });
    }

    case ACTION_TYPES.FORM_PAYMENT_ACTIVATION_AVAILABLE_EWALLET: {
      return update(state, {
        activeEWallet: {
          isLoading: {
            $set: true,
          },
        },
      });
    }

    case ACTION_TYPES.FORM_PAYMENT_ACTIVATION_AVAILABLE_EWALLET_SUCCESS: {
      return update(state, {
        activeEWallet: {
          data: {
            $set: {
              availableEWallets: payload.data.available_ewallets,
              isQrisRegistered: payload.data.is_qris_registered,
            },
          },
          isLoading: {
            $set: false,
          },
        },
      });
    }

    case ACTION_TYPES.FORM_PAYMENT_ACTIVATION_AVAILABLE_EWALLET_FAILED: {
      return update(state, {
        activeEWallet: {
          error: {
            $set: payload.message,
          },
          isLoading: {
            $set: false,
          },
        },
      });
    }
    default:
      return state;
  }
};
